import { useInfiniteQuery } from '@tanstack/react-query';

import { getNextPageParam } from '@/lib/constants/pagination';
import { mapMemberStatusToDTO } from '@/modules/common/mappers/member-status.mapper';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import { getMemberAccess } from '../../api/member-access.api';

interface UseMemberAccessParams {
  filters?: {
    q?: string;
    groupIds?: string | string[];
    statuses?: string | string[];
  };
  enabled?: boolean;
}

/**
 * Maps UI filter values to API filter format
 * Handles both string and array values for groupIds and statuses
 */
const mapMemberAccessFilters = (filters: UseMemberAccessParams['filters']) => {
  const apiFilters: Record<string, any> = {};

  if (filters?.q) {
    apiFilters.search = filters.q;
  }

  if (filters?.groupIds) {
    const groupIdsArray = Array.isArray(filters.groupIds) ? filters.groupIds : [filters.groupIds];
    apiFilters.roleIds = groupIdsArray.map(id => Number(id));
  }

  if (filters?.statuses) {
    const statusesArray = Array.isArray(filters.statuses) ? filters.statuses : [filters.statuses];
    apiFilters.statuses = statusesArray.map(status => mapMemberStatusToDTO(status as any));
  }

  return apiFilters;
};

/**
 * Hook for fetching team members with user group assignments
 * Supports filtering and cursor-based pagination
 */
export const useMemberAccess = ({ filters, enabled = true }: UseMemberAccessParams = {}) => {
  return useInfiniteQuery({
    queryKey: ['member-access', filters],
    enabled,
    queryFn: ({ pageParam }) => {
      const apiFilters = mapMemberAccessFilters(filters);
      return getMemberAccess({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    staleTime: 10 * 60 * 1000,
    initialPageParam: undefined,
    select: data => {
      const allItems = data.pages.flatMap(page => page.items);
      const seen = new Set<number>();
      const uniqueItems = allItems.filter(item => {
        if (seen.has(item.userId)) return false;
        seen.add(item.userId);
        return true;
      });
      return new PaginatedResponse({
        items: uniqueItems,
        pagination: data.pages[data.pages.length - 1]?.pagination,
      });
    },
  });
};
